import { z } from "zod";
import { FirebaseDocument, UnparsedFirebaseDocument, ParsedFirebaseDocument, ParsedFirebaseCollection } from "./firebaseLoaders.types";
import {
  collection,
  CollectionReference,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  Query,
  QueryConstraint,
  WhereFilterOp,
} from "firebase/firestore";

/**
 * A function that loads a document from firebase and parses it with a zod type
 * @param path path to the document
 * @param zodType zod type to parse the document with
 * @returns The parsed document if successful
 */
export const loadParsedFirebaseDocument = async <T extends z.ZodType<any, any, any>>(path: string, zodType: T): Promise<FirebaseDocument<T>> => {
  const reference = doc(getFirestore(), path);
  const id = reference.id;

  const document = await getDoc(reference);
  const exists = document.exists();

  const unparsedDoc: UnparsedFirebaseDocument<T> = {
    id,
    exists,
    valid: false,
    path,
    errors: [],
  };

  // If the document exists, return a ParsedFirebaseDocument
  if (exists) {
    const parsed = zodType.safeParse({ id, ...document.data() });

    if (parsed.success) {
      const parsedDoc: ParsedFirebaseDocument<T> = {
        id,
        exists,
        valid: true,
        path,
        data: parsed.data,
      };
      return parsedDoc;
    }

    console.warn(`Failed to parse ${path}`, JSON.stringify(parsed.error));
    unparsedDoc.errors.push(...parsed.error.errors);
  }

  // If the document doesn't exist or parsing fails, return a UnparsedFirebaseDocument
  return unparsedDoc;
};

/**
 * A function that loads a collection from firebase and parses all documents that are valid with a zod type
 * @param path path to the collection
 * @param documentType zod type per document
 * @returns A list of the valid documents
 */
export const loadParsedFirebaseCollection = async <T extends z.ZodType<any, any, any>>(
  path: string,
  zodType: T
): Promise<ParsedFirebaseCollection<T>> => {
  let parsedCollection: ParsedFirebaseCollection<T> = {
    docs: [],
    exists: false,
    path,
  };

  const result = await getDocs(collection(getFirestore(), path));
  parsedCollection.exists = !result.empty;
  if (parsedCollection.exists) {
    result.forEach((document) => {
      const exists = document.exists();
      if (exists) {
        const id = document.id;
        const parsed = zodType.safeParse({ id, ...document.data() });
        if (parsed.success) {
          parsedCollection.docs.push({
            id,
            data: parsed.data,
            exists,
            valid: true,
            path: document.ref.path,
          });
        } else {
          console.warn(`Failed to parse: ${document.ref.path}`, JSON.stringify(parsed.error));
        }
      }
    });
  }

  return parsedCollection;
};

export const loadParsedFirebaseCollectionWhere = async <T extends z.ZodType<any, any, any>>(
  path: string,
  zodType: T,
  ...where: QueryConstraint[]
): Promise<ParsedFirebaseCollection<T>> => {
  const result = await getDocs(query(collection(getFirestore(), path), ...where));

  return {
    docs: result.docs
      .filter((doc) => doc.exists())
      .map((doc) => {
        const parsed = zodType.safeParse({ id: doc.id, ...doc.data() });
        return parsed.success
          ? { id: doc.id, data: parsed.data, exists: true, valid: true, path: doc.ref.path }
          : (console.warn(`Failed to parse: ${doc.ref.path}`, JSON.stringify(parsed.error)), null);
      })
      .filter(Boolean) as ParsedFirebaseCollection<T>["docs"], // Remove nulls
    exists: !result.empty,
    path,
  };
};
